import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { VENUE_PATHS } from "Venues/router";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { createStage, dataMetaSelector } from "Venues/state";
import { StageForm } from "Venues/components";
import { IStage, IStageCapacity, VENUE_ACTIONS } from "Venues/types";
import { useVenuePageContext } from "Venues/contexts";
import { CreateStageCapacity } from "./CreateStageCapacity";
import { useClearActionMetaOnUnMount, useVenueRoute } from "Venues/utils";

const buttons: IFormButtons = {
  primary: {
    "data-testid": "create_stage_button",
    children: copy.createStage.cta,
    size: "huge",
    isFullWidth: true,
  },
};

interface ICreateStageForm {
  updatePlaceholder?: (stage: IStage) => void;
}

export const CreateStage: React.FC<ICreateStageForm> = props => {
  const { getPath } = useVenueRoute();
  const [stageCapacities, setStageCapacities] = useState<IStageCapacity[]>([]);

  const { venueId, fetchVenue } = useVenuePageContext();
  const { api: apiMeta, id: stageId } = useSelector(dataMetaSelector(VENUE_ACTIONS.createStage));
  const { api: apiCapacityMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.createStageCapacity));
  const dispatch = useDispatch();
  const history = useHistory();
  useClearActionMetaOnUnMount(VENUE_ACTIONS.createStage);
  useClearActionMetaOnUnMount(VENUE_ACTIONS.createStageCapacity);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && stageId) {
      fetchVenue();

      let path = getPath(VENUE_PATHS.STAGE, { stageId });
      let toastMessage: { message: string; status: IStatus };

      if (apiCapacityMeta?.status === IAPIState.failed) {
        if (stageId) {
          path = getPath(VENUE_PATHS.EDIT_STAGE, { stageId });
        }
        toastMessage = {
          message: copy.createStage.failed,
          status: IStatus.error,
        };
      } else {
        toastMessage = {
          message: copy.createStage.success,
          status: IStatus.success,
        };
      }
      history.push(path);
      dispatch(addTemporaryToast(toastMessage));
    }
  }, [dispatch, history, apiMeta, apiCapacityMeta, fetchVenue, stageId, getPath]);

  const handleSubmit = (partialStage: IStage) => {
    const stage: IStage = { ...partialStage, stageCapacities };
    dispatch(createStage({ venueId, stage }));
  };

  return (
    <StageForm
      hasError={!!apiMeta?.error}
      apiStatus={apiMeta?.status}
      handleSubmit={handleSubmit}
      buttons={buttons}
      {...props}
    >
      <CreateStageCapacity capacities={stageCapacities} setCapacities={setStageCapacities} />
    </StageForm>
  );
};
